import { env } from '@/env';
import { getIsMobilePlatform } from '@/utils/device';

declare global {
  interface Window {
    Forethought: (key: string, value: string) => void;
  }
}

const CHATBOT_SCRIPT_ID = 'forethought-widget-embed-script';
const CHATBOT_WIDGET_BUTTON_IMAGE = 'https://join.helloheart.com/chatbot.svg';

const MOBILE_OFFSET_Y = '120px';
const DESKTOP_OFFSET_Y = '90px';

type ChatBotConfig = Map<string, string>;

const offsetY = getIsMobilePlatform() ? MOBILE_OFFSET_Y : DESKTOP_OFFSET_Y;
const positionY = getIsMobilePlatform() ? 'top' : 'bottom';

const defaultAttributes = new Map<string, string>([
  ['src', 'https://solve-widget.forethought.ai/embed.js'],
  ['id', CHATBOT_SCRIPT_ID],
  ['data-api-key', env.FORETHOUGHT_API_KEY],
  ['config-ft-widget-button-image', CHATBOT_WIDGET_BUTTON_IMAGE],
  ['emit-tracking-events', 'true'],
  ['offset-x', '0px'],
  ['offset-y', offsetY],
  ['position-y', positionY],
  ['position-x', 'right'],
  ['data-ft-embed-script-language', 'en'],
  // ['hidden', 'true'], // TODO future - Uncomment this line to hide chatbot by default
]);

const isAttributesEqual = (existingScriptTag: HTMLScriptElement, newConfig: ChatBotConfig) => {
  for (const [key, value] of newConfig) {
    if (existingScriptTag.getAttribute(key) !== value) {
      return false;
    }
  }
  return true;
};

let retryTimeoutPointer: number;
let existingScriptTag: HTMLScriptElement | null = null;

export const chatBotManager = {
  init: (config: Map<string, string> = new Map()) => {
    const combinedConfig = new Map([...defaultAttributes, ...config]);
    if (existingScriptTag && isAttributesEqual(existingScriptTag, combinedConfig)) {
      return;
    }

    existingScriptTag?.remove();
    if (document.querySelector(`#${CHATBOT_SCRIPT_ID}`)) {
      document.querySelector(`#${CHATBOT_SCRIPT_ID}`)?.remove();
    }

    const script = document.createElement('script');
    for (const [key, value] of combinedConfig) {
      script.setAttribute(key, value);
    }
    existingScriptTag = script;

    document.body.prepend(script);
  },
  open: () => {
    chatBotManager.setWidgetVisibility(true);
    if (window.Forethought) {
      window.Forethought('widget', 'open');
    }
  },
  close: () => {
    if (window.Forethought) {
      window.Forethought('widget', 'close');
    }
  },
  setWidgetVisibility: (isVisible: boolean) => {
    if (window.Forethought) {
      window.Forethought('widget', isVisible ? 'show' : 'hide');
    } else {
      clearTimeout(retryTimeoutPointer);
      retryTimeoutPointer = window.setTimeout(() => {
        chatBotManager.setWidgetVisibility(isVisible);
      }, 1000);
    }
  },
};
